/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {getUserUnit} from '@ion/core/src/units/helpers';
import Unit from '@ion/core/src/units/units';
import {GeofenceRadius} from './GeofenceRadiusControl';
import {VicinitySearchCard} from './VicinitySearchCard';

export type GeofenceSearchVicinityCardProps = {
  search: ReactNode;
  radius: number;
  sliderProps: Pick<GeofenceRadius, 'max' | 'step' | 'marks'>;
  hours: number;
  isSearchDisabled: boolean;
  onSearch: () => void;
  onClose: () => void;
  onHoursChange: (hours: number) => void;
  onRadiusSlide: GeofenceRadius['onSlide'];
  onRadiusInput: GeofenceRadius['onInputChange'];
};

// the vicinity card labels only the ends and middle of the slider's range ...
const CARD_MARK_INTERVAL = 5;

/**
 * Renders GeofenceSearch's vicinity step as the current theme's vicinity
 * search card. The vicinity radius is already in the user's unit (miles or
 * kilometers), and the card labels only the ends and middle of the
 * slider's own marks.
 *
 * ```typescript
 *
 *   import {GeofenceSearchVicinityCard} from
 *     '@ion/dls/src/maps/geofences/GeofenceSearchVicinityCard';
 *   ...
 *
 *   <GeofenceSearchVicinityCard search={<SearchAddress mapView={view} hideLabel />}
 *     radius={3}
 *     sliderProps={sliderProps}
 *     hours={8}
 *     isSearchDisabled={!drawingComplete}
 *     onSearch={onSearch}
 *     onClose={onClose}
 *     onHoursChange={onHoursChange}
 *     onRadiusSlide={onRadiusSlide}
 *     onRadiusInput={onRadiusInput} />
 *
 * ```
 *
 * @param   search           The address search field.
 * @param   radius           The vicinity radius, in the user's unit.
 * @param   sliderProps      The radius slider's range and marks.
 * @param   hours            The number of past hours to display.
 * @param   isSearchDisabled Disables the Search button when `true`.
 * @param   onSearch         Invoked by the Search button.
 * @param   onClose          Invoked by the close button.
 * @param   onHoursChange    Invoked with the newly selected number of hours.
 * @param   onRadiusSlide    Invoked with the radius the slider moved to.
 * @param   onRadiusInput    Invoked when the radius is typed.
 * @returns                  The vicinity search card.
 */
export const GeofenceSearchVicinityCard = ({
  search,
  radius,
  sliderProps,
  hours,
  isSearchDisabled,
  onSearch,
  onClose,
  onHoursChange,
  onRadiusSlide,
  onRadiusInput
}: GeofenceSearchVicinityCardProps) => {
  const unitKey   = getUserUnit() === Unit.METRIC ? 'kilometers' : 'miles';
  const cardMarks = [{value: 0, label: '0'}, ...sliderProps.marks.filter(function(mark) {
    return mark.value % CARD_MARK_INTERVAL === 0;
  })];

  return (
    <VicinitySearchCard onClose={onClose}
      onSearch={onSearch}
      isSearchDisabled={isSearchDisabled}
      hours={hours}
      onHoursChange={onHoursChange}
      search={search}
      radius={{
        value         : radius,
        max           : sliderProps.max,
        step          : sliderProps.step,
        marks         : cardMarks,
        unitLabel     : translate(`maps:geofences.${unitKey}`),
        onSlide       : onRadiusSlide,
        onInputChange : onRadiusInput
      }} />
  );
};

export default GeofenceSearchVicinityCard;
